import { ValueType } from 'react-native-dropdown-picker';
import { FormChildProp } from '@/components/Form/types';
import React, { useEffect, useState } from 'react';
import { Icon, Text, useTheme } from 'react-native-paper';
import { Pressable, StyleProp, StyleSheet, TextStyle, View, ViewStyle } from 'react-native';
import { useWatch } from 'react-hook-form';
import { Border, Color, Padding } from '@/GlobalStyles';
import { useTranslation } from 'react-i18next';
import { Implant } from '@/data/data';
import HelpButton from '@/components/HelpButton';

const FavoriteInput = React.forwardRef(
  <T extends ValueType>(
    props: {
      data?: Implant[];
      labelStyle?: StyleProp<TextStyle>;
      containerStyle?: StyleProp<ViewStyle>;
      withMargin?: boolean;
      withBottomBorder?: boolean;
    } & FormChildProp & {
        defaultValue?: T | T[] | null;
        value?: T | T[] | null;
      },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ref
  ): React.ReactElement => {
    const {
      defaultValue,
      onChangeValue,
      error,
      value,
      name,
      label,
      data,
      withMargin,
      labelStyle,
      containerStyle,
      withBottomBorder = false,
      withHelpButton = false,
      helpFieldName,
    } = props;
    const theme = useTheme();

    const [valueSet, setValueSet] = useState<T | T[] | null | undefined>(value || defaultValue);

    const currentValue = useWatch({ name: name, defaultValue });

    const { t } = useTranslation();

    useEffect(() => {
      if (onChangeValue && valueSet !== value) {
        onChangeValue(
          valueSet !== null && valueSet !== undefined
            ? valueSet
            : value !== null && value !== undefined
              ? value
              : defaultValue || undefined
        );
      }
    }, [valueSet]);

    useEffect(() => {
      if (valueSet !== currentValue) {
        setValueSet(currentValue);
      }
    }, [currentValue]);

    return (
      <View
        style={[
          styles.container,
          withMargin
            ? {
                marginBottom: 10,
              }
            : null,
          containerStyle,
        ]}
      >
        {label ? (
          withHelpButton && (helpFieldName || name) ? (
            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 4 }}>
              <Text variant={'labelMedium'} style={[styles.label, { marginBottom: 0 }, labelStyle]}>
                {t(label)}
              </Text>
              <HelpButton
                fieldName={helpFieldName || name || ''}
                size={16}
                style={{ marginLeft: 4 }}
              />
            </View>
          ) : (
            <Text variant={'labelMedium'} style={[styles.label, labelStyle]}>
              {t(label)}
            </Text>
          )
        ) : null}
        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'flex-start',
            rowGap: 5,
            flexWrap: 'wrap',
          }}
        >
          {data
            ? data.map(({ id, name: implantName }) => (
                <Pressable
                  key={name + '_' + id}
                  onPress={() => {
                    setValueSet(id);
                  }}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    marginHorizontal: 5,
                    borderWidth: 1,
                    backgroundColor: id === valueSet ? Color.MainBleu : Color.blanc,
                    borderColor: Color.MainBleu,
                    borderRadius: Border.br_31xl,
                    paddingVertical: 7,
                    paddingBottom: 5,
                    paddingHorizontal: 9,
                  }}
                >
                  {id === valueSet ? (
                    <View style={{ position: 'relative', top: -2, left: -4 }}>
                      <Icon size={15} source={'check'} color={Color.blanc} />
                    </View>
                  ) : null}
                  <Text
                    variant={'labelSmall'}
                    style={{ color: id !== valueSet ? Color.MainBleu : Color.blanc }}
                  >
                    {implantName}
                  </Text>
                  <View style={{ position: 'relative', top: -2, left: 4 }}>
                    <Icon size={15} source={'star'} color={Color.sECJAUNE} />
                  </View>
                </Pressable>
              ))
            : null}
        </View>
        {error?.message ? (
          <Text
            variant={'labelMedium'}
            style={{
              color: theme.colors.error,
              marginLeft: 18,
              bottom: -18,
              flex: 1,
              position: 'absolute',
            }}
          >
            {props.error?.message}
          </Text>
        ) : null}
        {withBottomBorder ? (
          <View style={{ marginTop: 10, height: 1, backgroundColor: Color.MainBleu }} />
        ) : null}
      </View>
    );
  }
);

export default FavoriteInput;

const styles = StyleSheet.create({
  container: {
    flexDirection: 'column',
  },
  label: {
    paddingBottom: 20,
  },
});
